import { MediaElement } from './MediaElement.class';
import type { Audio } from './part/Audio_howl.class';
import { audioPool, type AudioLease } from './part/AudioPool.class';

export class SoundElement extends MediaElement {
  private audio: Audio | null = null;
  private audioLease: AudioLease | null = null;
  private readonly preferHtml5: boolean;

  constructor(options?: { preferHtml5?: boolean }) {
    super();
    this.preferHtml5 = options?.preferHtml5 ?? false;
  }

  public override async load() {
    if (!this._data.content || typeof this._data.content !== 'string') return false;

    const resource = this._data.content;
    this.releaseAudio();
    const lease = audioPool.acquire(resource, this.preferHtml5);
    const audio = lease.audio;
    this.audioLease = lease;
    this.audio = lease.audio;

    try {
      const loaded = await lease.loaded;
      if (this.audioLease !== lease) {
        lease.release();
        return false;
      }
      if (!loaded) {
        this.releaseAudio(lease);
        return false;
      }

      await this.applyConfig();

      return this.audioLease === lease && this.audio === audio;
    } catch (error) {
      this.releaseAudio(lease);
      console.error(`Sound Element 加载失败: url:${resource} - ${error}`);
      return false;
    }
  }

  /**
   * 重新加载音频：释放旧音频租约并以最新的 _data.content 重新加载。
   * 用于 ACTION_RELOAD 等场景下替换音频 URL 后重新获取 AudioPool 中的实例。
   */
  public override async reload(): Promise<boolean> {
    this.releaseAudio();
    return this.load();
  }

  private releaseAudio(lease = this.audioLease): void {
    if (!lease) return;
    if (this.audioLease === lease) {
      this.audioLease = null;
      this.audio = null;
    }
    lease.release();
  }

  /**
   * 更新媒体相关配置，如果调用裁剪功能，调用时务必await，不然时长会对不上
   * @returns
   */
  public override async applyConfig() {
    if (!this.mediaConfig || !this.audio) return;

    if (this.mediaConfig.offsetStart !== undefined || this.mediaConfig.offsetEnd !== undefined) {
      await this.audio?.updateOffsetTime(
        this.mediaConfig.offsetStart ?? 0,
        this.mediaConfig.offsetEnd || -1
      );
    }

    if (this.mediaConfig.loop !== undefined) {
      this.audio.loop = this.mediaConfig.loop;
    }

    if (this.mediaConfig.muted !== undefined) {
      this.audio.muted = this.mediaConfig.muted;
    }

    if (this.mediaConfig.volume !== undefined) {
      this.audio.volume = this.mediaConfig.volume;
    }
  }

  public override seek(time: number): void {
    if (!this.audio) return;

    this.audio.seek(time);
  }

  public override seekPlay(_time: number): void {
    if (!this.audio || this.playing) return;

    super.seekPlay(_time);
    this.audio.seekPlay(_time);
  }

  /**
   * 重头播放：先停止当前播放再从指定时间播放。
   * Audio.playAudio 在已播放时会跳过 seekPlay，重复触发的短音效需先 pause 才能重播。
   */
  public restart(time = 0): void {
    this.stop();
    this.seekPlay(time);
  }

  public resume(): void {
    this.audio?.seekPlay(this.audio.currentTime);
  }

  public setPlaybackGain(value: number): void {
    this.audio?.setPlaybackGain(value);
  }

  public prepareForAutoplay(): void {
    this.audio?.prepareForAutoplay();
  }

  public prepareForUserGesture(): void {
    this.audio?.prepareForUserGesture();
  }

  public setLoop(loop: boolean): void {
    if (this.audio) this.audio.loop = loop;
  }

  public setPlaybackBlockedHandler(handler: (() => void) | null): void {
    this.audio?.setPlaybackBlockedHandler(handler);
  }

  public setPlaybackStartedHandler(handler: (() => void) | null): void {
    this.audio?.setPlaybackStartedHandler(handler);
  }

  public setPlaybackEndedHandler(handler: (() => void) | null): void {
    this.audio?.setPlaybackEndedHandler(handler);
  }

  public setPlaybackErrorHandler(handler: ((error: unknown) => void) | null): void {
    this.audio?.setPlaybackErrorHandler(handler);
  }

  public override stop(): void {
    super.pause();
    this.audio?.stop();
  }

  public override pause(): void {
    super.pause();

    this.audio?.pause();
  }

  public override destroy(): void {
    this.releaseAudio();

    super.destroy();
  }

  public override get mediaDuration() {
    return this.audio?.duration || 0;
  }

  public override get playing(): boolean {
    const state = this.audio?.state;
    return state === 'starting' || state === 'playing' || state === 'waiting';
  }
}
